<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="head-icons ms-2">
          <a
            href="javascript:void(0);"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-original-title="Collapse"
            id="collapse-header"
            @click="toggleHeader"
          >
            <i class="ti ti-chevrons-up"></i>
          </a>
        </div>
      </div>

      <div class="card dq-search-hero">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div class="col-lg-9">
              <label class="form-label fw-semibold">Search Query</label>
              <div class="input-group input-group-flat">
                <span class="input-icon-addon">
                  <i class="ti ti-search"></i>
                </span>
                <input
                  v-model.trim="searchInput"
                  type="text"
                  class="form-control"
                  placeholder="Search pages, employees, tickets, projects, clients, subscriptions..."
                  @keyup.enter="submitSearch"
                />
              </div>
              <p class="text-muted mb-0 mt-2">
                Results include accessible pages and records in your current scope.
              </p>
            </div>
            <div class="col-lg-3 d-flex gap-2 justify-content-lg-end">
              <button class="btn btn-light" type="button" @click="clearSearch">
                Clear
              </button>
              <button class="btn btn-primary" type="button" @click="submitSearch">
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3 mb-3">
        <div class="col-md-4">
          <div class="card shadow-sm h-100">
            <div class="card-body">
              <span class="dq-stat-label">Query</span>
              <h5 class="mb-0 mt-2 text-truncate">
                {{ activeQuery || "No active query" }}
              </h5>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card shadow-sm h-100">
            <div class="card-body">
              <span class="dq-stat-label">Sections</span>
              <h3 class="mb-0 mt-2">{{ sections.length }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card shadow-sm h-100">
            <div class="card-body">
              <span class="dq-stat-label">Results</span>
              <h3 class="mb-0 mt-2">{{ total }}</h3>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="card">
        <div class="card-body py-5 text-center text-muted">
          <i class="ti ti-loader-2 ti-spin fs-22 me-2"></i>
          Searching...
        </div>
      </div>

      <div v-else-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <div
        v-else-if="!activeQuery"
        class="card border-0 shadow-sm dq-empty-search"
      >
        <div class="card-body text-center py-5">
          <div class="dq-empty-icon">
            <i class="ti ti-search"></i>
          </div>
          <h5 class="mb-2">Start with a search</h5>
          <p class="text-muted mb-0">
            Use the header search or type a query above.
          </p>
        </div>
      </div>

      <div
        v-else-if="!sections.length"
        class="card border-0 shadow-sm dq-empty-search"
      >
        <div class="card-body text-center py-5">
          <div class="dq-empty-icon dq-empty-icon-warning">
            <i class="ti ti-file-search"></i>
          </div>
          <h5 class="mb-2">No results found</h5>
          <p class="text-muted mb-0">
            Nothing matched
            <span class="fw-semibold">"{{ activeQuery }}"</span>
            in your accessible records.
          </p>
        </div>
      </div>

      <div v-else class="d-grid gap-3">
        <div
          v-for="section in sections"
          :key="section.key"
          class="card border-0 shadow-sm"
        >
          <div
            class="card-header bg-white border-0 d-flex align-items-center justify-content-between flex-wrap gap-2"
          >
            <div class="d-flex align-items-center gap-2">
              <span class="dq-section-icon">
                <i :class="`ti ti-${section.icon || 'search'}`"></i>
              </span>
              <div>
                <h5 class="mb-0">{{ section.label }}</h5>
                <small class="text-muted">
                  {{ section.items?.length || 0 }} result(s)
                </small>
              </div>
            </div>
          </div>
          <div class="card-body pt-0">
            <div class="list-group list-group-flush">
              <button
                v-for="item in section.items"
                :key="`${section.key}-${item.id}`"
                type="button"
                class="list-group-item list-group-item-action dq-search-item"
                @click="openItem(item)"
              >
                <div class="dq-search-item-main">
                  <div class="dq-search-item-head">
                    <span class="dq-type-pill">{{ formatType(item.type) }}</span>
                    <h6 class="mb-0">{{ item.title }}</h6>
                  </div>
                  <p v-if="item.subtitle" class="dq-search-subtitle mb-1">
                    {{ item.subtitle }}
                  </p>
                  <p v-if="item.description" class="dq-search-description mb-0">
                    {{ item.description }}
                  </p>
                </div>
                <i class="ti ti-arrow-up-right dq-search-arrow"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { useAccessStore } from "@/stores/accessStore";
import layoutHeader from "@/views/layouts/layout-header.vue";
import layoutSidebar from "@/views/layouts/layout-sidebar.vue";
import globalSearchService from "@/services/globalSearch.service";
import {
  findAccessiblePages,
  mergeSearchSections,
} from "@/utils/globalSearchPages";

const title = "Search Result";
const text = "Pages";
const text1 = "Search Result";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const accessStore = useAccessStore();

const loading = ref(false);
const error = ref("");
const total = ref(0);
const sections = ref([]);
const searchInput = ref("");
const activeQuery = computed(() => String(route.query.q || "").trim());

function toggleHeader() {
  const headerCollapse = document.getElementById("collapse-header");
  if (headerCollapse) headerCollapse.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function normalizeResponse(query, payload) {
  total.value = Number(payload?.total || 0);
  const pageItems = findAccessiblePages(query, authStore, accessStore, 8);
  sections.value = mergeSearchSections(
    pageItems,
    Array.isArray(payload?.sections) ? payload.sections : [],
  );
  total.value = sections.value.reduce(
    (sum, section) => sum + (Array.isArray(section.items) ? section.items.length : 0),
    0,
  );
}

async function fetchSearch(query) {
  const q = String(query || "").trim();
  if (!q) {
    total.value = 0;
    sections.value = [];
    error.value = "";
    return;
  }

  loading.value = true;
  error.value = "";
  try {
    await accessStore.ensureAccess();
    const res = await globalSearchService.search({ q, limit: 8 });
    normalizeResponse(q, res.data || {});
  } catch (err) {
    error.value =
      err?.response?.data?.message || "Failed to load search results.";
    total.value = 0;
    sections.value = [];
  } finally {
    loading.value = false;
  }
}

async function submitSearch() {
  const q = searchInput.value.trim();
  if (!q) {
    await router.push({ path: "/pages/search-result" });
    return;
  }

  await router.push({
    path: "/pages/search-result",
    query: { q },
  });
}

async function clearSearch() {
  searchInput.value = "";
  await router.push({ path: "/pages/search-result" });
}

async function openItem(item) {
  if (!item?.route) return;
  await router.push(item.route);
}

function formatType(type) {
  return String(type || "result")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

watch(
  () => route.query.q,
  async (q) => {
    searchInput.value = String(q || "");
    await fetchSearch(q);
  },
  { immediate: true },
);

onMounted(() => {
  searchInput.value = activeQuery.value;
});
</script>

<style scoped>
.input-group-flat {
  position: relative;
}

.input-icon-addon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  pointer-events: none;
}

.input-group-flat .form-control {
  padding-left: 42px;
}

.dq-search-hero {
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.04);
}

.dq-stat-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
}

.dq-empty-search {
  border: 1px dashed rgba(15, 23, 42, 0.12);
}

.dq-empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  margin: 0 auto 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #0d6efd;
  background: rgba(13, 110, 253, 0.08);
}

.dq-empty-icon-warning {
  color: #f97316;
  background: rgba(249, 115, 22, 0.08);
}

.dq-section-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.06);
  color: #0f172a;
  font-size: 18px;
}

.dq-search-item {
  border: 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  padding: 16px 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background: transparent;
}

.dq-search-item:last-child {
  border-bottom: 0;
}

.dq-search-item:hover {
  background: rgba(15, 23, 42, 0.02);
}

.dq-search-item-main {
  min-width: 0;
  flex: 1;
}

.dq-search-item-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  min-width: 0;
}

.dq-search-item-head h6 {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dq-type-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #475569;
  background: rgba(148, 163, 184, 0.16);
}

.dq-search-subtitle {
  color: #0f172a;
  font-weight: 600;
}

.dq-search-description {
  color: #64748b;
  font-size: 13px;
}

.dq-search-arrow {
  color: #94a3b8;
  margin-top: 4px;
}
</style>
